import React, { useCallback, useEffect, useState } from "react";
import { WarningFilled } from "@ant-design/icons";
import { Button, Form, Modal, Spin, notification } from "antd";
import { unsubscribeReasons } from "../../utils/data";
import DataProvider from "../../services";
import { useNavigate } from "react-router-dom";
import Empty from "../../components/elements/empty";

// Must match API `UNSUBSCRIBE_ERROR_CODES` in api-medpharma
// (src/subscriptions/constants/unsubscribe-errors.ts).
const UNSUBSCRIBE_ERROR_CODES = {
  requiresSupport: "SUBSCRIPTION_UNSUBSCRIBE_REQUIRES_SUPPORT",
  providerUnavailable: "SUBSCRIPTION_UNSUBSCRIBE_PROVIDER_UNAVAILABLE",
};

// Same support line already used in header.tsx / footer.tsx.
const SUPPORT_TEL_LINK = "tel:+233303000448";

export default function Unsubscribe() {
  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [reasons, setReasons] = useState<string[]>([]);
  const [data, setData] = useState<any>(null);
  const [confirmOpen, setConfirmOpen] = useState<boolean>(false);

  const checking = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await DataProvider.getUserSubscription();
      setData(data);
      if (data.message && data.message === "Not a subscriber!") {
        setLoading(false);
        api["info"]({
          message: "Mcare",
          description: "You are not on any Mcare Plan!",
        });
        return;
      }
      if (
        data.status &&
        (data.status === "pending" || data.status === "activation_failed")
      ) {
        setLoading(false);
        return api["info"]({
          message: "Mcare",
          description: "You are not on any Mcare Plan!",
        });
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      return api["warning"]({
        message: "Warning",
        description:
          "Sorry couldn't verify your mcare package, kindly click on the refresh button to refresh!",
      });
    }
  }, [api]);

  useEffect(() => {
    checking();
  }, [checking]);

  function onChacked(e: React.ChangeEvent<HTMLInputElement>, params: string) {
    const checked = e.target.checked;
    if (checked) {
      setReasons([...reasons, params]);
    } else {
      const arr = reasons.filter((value) => value !== params);
      setReasons(arr);
    }
  }

  async function checkReasonSelected() {
    const values = await form.getFieldsValue();
    if (
      reasons.length < 1 &&
      (values.unsubscribeReason === undefined ||
        values.unsubscribeReason.length < 1)
    ) {
      api["warning"]({
        message: "Warning",
        description: "Select one or type the reason for unsubscribing.",
      });
      return false;
    }
    return true;
  }

  async function onUnsubscribeClick() {
    if (await checkReasonSelected()) {
      setConfirmOpen(true);
    }
  }

  async function unsubscribe() {
    const values = await form.getFieldsValue();
    const payload = {
      unsubscribeReason: values.unsubscribeReason,
      unsubscribeReasonList: reasons,
      subscriptionId: data.id,
    };
    try {
      setConfirmOpen(false);
      setLoading(true);
      await DataProvider.unsubscribe(payload);
      setLoading(false);
      api["success"]({
        message: "Unsubscribed",
        description: "Successfully unsubscribed.",
      });
      navigate("/main/profile/2");
    } catch (error: any) {
      setLoading(false);
      const code = error?.response?.data?.code;
      const serverMessage = error?.response?.data?.message;
      const needsSupportCall =
        code === UNSUBSCRIBE_ERROR_CODES.requiresSupport ||
        code === UNSUBSCRIBE_ERROR_CODES.providerUnavailable;

      return api["error"]({
        message: "Failed",
        description:
          needsSupportCall && serverMessage
            ? serverMessage
            : "Unsubscription failed, try again later.",
        duration: needsSupportCall ? 0 : 4.5,
        btn: needsSupportCall ? (
          <Button
            type="primary"
            size="small"
            style={{ backgroundColor: "#06509E" }}
            onClick={() => {
              window.location.href = SUPPORT_TEL_LINK;
              api.destroy();
            }}
          >
            Call support
          </Button>
        ) : undefined,
      });
    }
  }

  return (
    <div className="md:p-4 p-2">
      <div className="bg-white border border-solid border-gray-200 rounded-lg w-full min-h-[85vh] py-6 px-4 sm:px-6">
        {(!loading && !data) ||
        (data &&
          (data.status === "pending" ||
            data.status === "activation_failed" ||
            data.status === "cancelled")) ? (
          <div className="flex justify-center">
            <div className="lg:w-[40%] md:w-[60%] w-[90%] flex flex-col justify-center items-center">
              <Empty
                imageStyle="md:h-44 h-24"
                titleStyle="md:text-lg text-sm"
                desStyle="md:text-base"
                title="Mcare"
                buttons={[
                  {
                    name: "Refresh",
                    event: () => checking(),
                  },
                ]}
                description="You are not on any active plan."
              />
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="w-full max-w-lg">
              <div className="flex flex-col items-center text-center mb-6">
                <div className="h-14 w-14 rounded-full bg-red-50 text-red-600 flex items-center justify-center text-2xl mb-3">
                  <WarningFilled />
                </div>
                <h3 className="font-semibold text-xl text-neutral-900">
                  Unsubscribe from Mcare
                </h3>
                <p className="text-sm text-neutral-500 mt-1">
                  Before you go, let us know why - it helps us improve.
                </p>
              </div>

              <div className="rounded-xl border border-neutral-200 p-4 sm:p-5 mb-4">
                <p className="text-sm font-medium text-neutral-800 mb-3">
                  Reason for unsubscribing (optional)
                </p>
                <div className="space-y-2 mb-4">
                  {unsubscribeReasons.map((v, idx) => (
                    <label
                      key={idx}
                      className="flex items-center gap-2.5 py-1.5 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        id={`unsubscribe-reason-${idx}`}
                        onChange={(e) => onChacked(e, v)}
                        className="h-4 w-4 rounded border-neutral-300 text-[#06509E] focus:ring-[#06509E]"
                      />
                      <span className="text-sm text-neutral-700">{v}</span>
                    </label>
                  ))}
                </div>

                <Form form={form} name="basic" layout="vertical" autoComplete="off">
                  <Form.Item name="unsubscribeReason" label="Other reason" className="mb-0">
                    <textarea
                      rows={3}
                      placeholder="Type here (optional)"
                      className="w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#06509E]/20 focus:border-[#06509E] resize-none"
                    />
                  </Form.Item>
                </Form>
              </div>

              <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 mb-6">
                <p className="text-sm text-red-700 m-0">
                  By unsubscribing, you will{" "}
                  <strong>permanently</strong> lose access to your Mcare
                  account data, including all benefits and features.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3">
                <Button
                  onClick={onUnsubscribeClick}
                  danger
                  size="large"
                  className="w-full sm:w-auto"
                >
                  Unsubscribe
                </Button>
                <Button
                  onClick={() => navigate(-1)}
                  type="primary"
                  size="large"
                  style={{ backgroundColor: "#06509E" }}
                  className="w-full sm:flex-1"
                >
                  Keep my subscription
                </Button>
              </div>
            </div>
          </div>
        )}
        {contextHolder}
      </div>
      <Modal
        title="Confirm unsubscribe"
        open={confirmOpen}
        onOk={unsubscribe}
        onCancel={() => setConfirmOpen(false)}
        okText="Yes, unsubscribe"
        cancelText="No, keep my plan"
        okButtonProps={{ danger: true, loading }}
      >
        <p>
          Are you sure you want to unsubscribe? This action is permanent and
          you will lose access to your Mcare benefits and features.
        </p>
      </Modal>
      <Spin spinning={loading} fullscreen />
    </div>
  );
}
